import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type {
  Person,
  PersonWithAccount,
  CreatePersonRequest,
  IDDocumentType,
} from "../Component/Application/Persons/types"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    Object.assign(config.headers, getExhibitionHeader());
    }
    return config
  },
  (error) => Promise.reject(error),
)

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  total_pages: number
  current_page: number
  page_size: number
  results: T[]
}

interface GetAllPersonsParams {
  page?: number
  page_size?: number
  search?: string
}

export const PersonService = {
  // Get all persons with pagination and search support
  getAll: async (params?: GetAllPersonsParams): Promise<PaginatedResponse<PersonWithAccount>> => {
    // Build query parameters
    const queryParams = new URLSearchParams()

    if (params?.page) {
      queryParams.append("page", params.page.toString())
    }

    if (params?.page_size) {
      queryParams.append("page_size", params.page_size.toString())
    }

    if (params?.search && params.search.trim()) {
      queryParams.append("search", params.search.trim())
    }

    const url = queryParams.toString() ? `/api/accounts/persons/?${queryParams.toString()}` : "/api/accounts/persons/"

    const response = await api.get(url)
    return response.data
  },

  // Get person by ID
  getById: async (id: number): Promise<PersonWithAccount> => {
    const response = await api.get(`/api/accounts/persons/${id}/`)
    return response.data
  },

  // Create person with account
  create: async (data: CreatePersonRequest): Promise<PersonWithAccount> => {
    const response = await api.post(`/api/accounts/persons/create-with-account/`, data)
    return response.data
  },

  // Update person
  update: async (id: number, data: Partial<Person>): Promise<Person> => {
    const response = await api.put(`/api/accounts/persons/${id}/update/`, data)
    return response.data
  },

  // Delete person
  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/accounts/persons/${id}/delete/`)
  },

  // Toggle person status
  toggleStatus: async (id: number): Promise<Person> => {
    const response = await api.post(`/api/accounts/persons/${id}/toggle-status/`)
    return response.data
  },

  // Toggle account active status
  toggleAccountStatus: async (personId: number): Promise<PersonWithAccount> => {
    const response = await api.post(`/api/accounts/persons/${personId}/toggle-account-status/`)
    return response.data
  },

  // Resend verification email
  resendVerification: async (personId: number): Promise<{ message: string }> => {
    const response = await api.post(`/api/accounts/persons/${personId}/resend-verification/`)
    return response.data
  },
}

export const IDDocumentTypeService = {
  getAll: async (): Promise<IDDocumentType[]> => {
    const response = await api.get(`/api/accounts/get-id-document-types/`)
    return response.data
  },
}
